import { useState } from "react";
import { toast } from "sonner";
import { Mic, MicOff, Loader2, VolumeX, Volume2, Radio } from "lucide-react";
import { useAudioLevels } from "@/hooks/useAudioLevels";
import { studioApi, errMessage } from "@/lib/studioApi";

function Meter({ level = 0, muted }) {
  const pct = Math.round(level * 100);
  const tone = muted ? "bg-zinc-700" : level > 0.85 ? "bg-red-500" : level > 0.5 ? "bg-amber-400" : "bg-emerald-500";
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10" aria-hidden="true">
      <div className={`h-full rounded-full transition-[width] duration-100 ${tone}`} style={{ width: muted ? "0%" : `${Math.max(pct, level > 0 ? 4 : 0)}%` }} />
    </div>
  );
}

/**
 * Producer audio mixer: one row per connected source with a live meter and a
 * mute/unmute button. Mute is instant (host control in the room); unmute asks the
 * guest/camera page to turn its mic on, which our pages do automatically.
 */
export function AudioMixer({ meeting, joined, roster, registry, program, showId, disabled, meters = true }) {
  const levels = useAudioLevels(meeting, roster, joined && meters);
  const [pending, setPending] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const sources = program?.sources || [];

  const rows = roster.list.filter((e) => e.isSelf || !e.customId || registry.size === 0 || registry.has(e.customId));
  const remotes = rows.filter((e) => !e.isSelf && e.customId);

  const markPending = (ids) => {
    setPending((p) => ({ ...p, ...Object.fromEntries(ids.map((i) => [i, true])) }));
    setTimeout(() => setPending((p) => { const n = { ...p }; ids.forEach((i) => delete n[i]); return n; }), 3000);
  };

  const setMic = async (e, enabled) => {
    if (e.isSelf) {
      try { if (enabled) await meeting.self.enableAudio(); else await meeting.self.disableAudio(); } catch (err) { toast.error("Could not change your mic"); }
      return;
    }
    if (!enabled) { try { e.rtk.disableAudio(); } catch (err) { /* page will comply via the control message */ } }
    if (!e.customId) return;
    markPending([e.customId]);
    try {
      await studioApi.micControl(showId, [e.customId], enabled);
      const reg = registry.get(e.customId);
      if (enabled && reg?.kind === "camera") toast.message("Asked the phone for its mic", { description: "The operator may need to tap Allow the first time." });
    } catch (err) { toast.error(errMessage(err)); }
  };

  const bulk = async (enabled, keepIds = []) => {
    const targets = remotes.filter((e) => !keepIds.includes(e.customId));
    if (!targets.length) return;
    setBulkBusy(true);
    if (!enabled) targets.forEach((e) => { try { e.rtk.disableAudio(); } catch (err) { /* noop */ } });
    markPending(targets.map((e) => e.customId));
    try { await studioApi.micControl(showId, targets.map((e) => e.customId), enabled); }
    catch (err) { toast.error(errMessage(err)); }
    finally { setBulkBusy(false); }
  };

  if (!rows.length) return <p className="text-sm text-zinc-500">Sources appear here once the room connects.</p>;

  return (
    <div className="space-y-3" data-testid="audio-mixer">
      <div className="flex flex-wrap gap-1.5">
        <button type="button" disabled={disabled || bulkBusy || !remotes.length} onClick={() => bulk(false)} className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-medium text-zinc-200 hover:bg-white/10 disabled:opacity-40" data-testid="mixer-mute-all"><VolumeX className="h-3.5 w-3.5" /> Mute all others</button>
        <button type="button" disabled={disabled || bulkBusy || !remotes.length} onClick={() => bulk(false, sources)} className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-medium text-zinc-200 hover:bg-white/10 disabled:opacity-40" title="Mute everyone who is not on air" data-testid="mixer-mute-offair"><Radio className="h-3.5 w-3.5" /> Only on-air</button>
        <button type="button" disabled={disabled || bulkBusy || !remotes.length} onClick={() => bulk(true)} className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-medium text-zinc-200 hover:bg-white/10 disabled:opacity-40" data-testid="mixer-unmute-all"><Volume2 className="h-3.5 w-3.5" /> Unmute all</button>
      </div>
      <ul className="space-y-2">
        {rows.map((e) => {
          const reg = e.customId ? registry.get(e.customId) : null;
          const name = reg?.label || reg?.name || e.name || "Guest";
          const kind = reg?.kind || (e.isSelf ? "host" : "guest");
          const live = !!e.rtk?.audioEnabled;
          const onAir = program?.layout === "speaker" || (e.customId && sources.includes(e.customId));
          const busy = !!(e.customId && pending[e.customId]);
          return (
            <li key={e.peerId} className="rounded-lg border border-white/10 bg-[#0a0f17] px-3 py-2" data-testid={`mixer-row-${e.customId || e.peerId}`} data-mic={live ? "on" : "off"}>
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 truncate text-xs font-medium">
                    {name}{e.isSelf && <span className="text-zinc-500">(you)</span>}
                    {onAir && <span className="rounded bg-[#c9772e]/20 px-1 py-px text-[9px] font-bold uppercase tracking-wider text-[#e6a25f]">on air</span>}
                  </div>
                  <div className="truncate text-[10px] uppercase tracking-wider text-zinc-500">{kind}{reg?.room_cam ? " · room cam" : ""}{!live ? " · muted" : ""}</div>
                </div>
                <button type="button" disabled={disabled || busy} onClick={() => setMic(e, !live)} title={live ? "Mute" : "Unmute"} data-testid={`mixer-toggle-${e.customId || e.peerId}`}
                  className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition ${live ? "bg-white/10 text-zinc-100 hover:bg-white/20" : "bg-red-600 text-white hover:bg-red-500"} disabled:opacity-60`}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : live ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-1.5"><Meter level={meters ? (levels[e.peerId] || 0) : 0} muted={!live || !meters} /></div>
            </li>
          );
        })}
      </ul>
      <p className="text-[11px] text-zinc-500">Mute is instant. Unmute turns a guest’s or phone’s mic back on from their page (they see a short notice).</p>
    </div>
  );
}

export default AudioMixer;
